/**
 * Deploy identity + path safety for published sites.
 *
 * Shared because the deployId MUST be derived identically on both sides: the
 * browser computes it to name the upload, and the Worker RECOMPUTES it from the
 * stored manifest at activate and refuses to go live on a mismatch. If the two
 * derivations ever drift, every publish 409s -- so there is exactly one
 * definition, here, and a test asserts both sides import it.
 *
 * Platform-neutral: `crypto.subtle` and TextEncoder only. No node:*, no DOM.
 *
 * WHY NOT artifactHash: lock-manifest.js hashes `artifactState` -- pages,
 * sections, packs -- and deliberately excludes resolved asset bytes, generated
 * CSS/JS, and fonts. Two materially different sites can share one. The deployId
 * must cover the actual bytes being served, so it hashes the file manifest.
 *
 * DETERMINISM: no clock, no random, no iteration-order dependence (entries are
 * sorted, canonicalStringify sorts keys). An interrupted publish resumes under
 * the same id, and a content-identical republish is a zero-byte no-op. The
 * run-twice detector in publish-plan-tests.js pins this.
 */

import { canonicalStringify } from './collab-protocol.js';

/** Bumping this changes every deployId, so treat it as a cache-invalidation lever. */
export const DEPLOY_MANIFEST_VERSION = 1;

/** Hex chars kept from the sha256. 32 is 128 bits -- collision-free at any plausible scale. */
export const DEPLOY_ID_LENGTH = 32;

export const DEPLOY_ID_RE = /^[0-9a-f]{32}$/;
export const BLOB_HASH_RE = /^[0-9a-f]{64}$/;

const encoder = new TextEncoder();

export async function sha256Hex(input) {
  const bytes = typeof input === 'string' ? encoder.encode(input) : input;
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Normalize an export-relative path into an R2-safe site path.
 *
 * Returns null for anything that could escape the deploy root or resolve
 * ambiguously. Called on BOTH sides: the browser to build the manifest, the
 * Worker to validate it and again to map an incoming request. A path that
 * normalizes differently in those three places is a path-confusion bug, so the
 * function is total and side-effect free.
 *
 * @returns {string|null}
 */
export function normalizeSitePath(value) {
  let path = String(value == null ? '' : value).trim();
  if (!path) return null;
  // Backslashes are never legitimate here and are a classic traversal dodge.
  if (path.includes('\\')) return null;
  if (path.includes('\0')) return null;
  path = path.replace(/^\/+/, '').replace(/\/{2,}/g, '/');
  if (!path || path.endsWith('/')) return null;
  const segments = path.split('/');
  for (const segment of segments) {
    // '.' and '..' are rejected outright rather than resolved: resolving would
    // make two different manifest entries collapse onto one key.
    if (!segment || segment === '.' || segment === '..') return null;
  }
  // Percent-encoded traversal, checked after a single decode. We do NOT return
  // the decoded form -- export paths are already literal.
  let decoded;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return null; // malformed escape
  }
  if (decoded.includes('..') || decoded.includes('\\') || decoded.includes('\0')) return null;
  if (path.length > 1024) return null;
  return path;
}

/**
 * Canonical manifest entries: sorted by path, only the three fields that define
 * identity. Throws on an invalid path so a bad manifest fails loudly at build
 * time rather than producing a deployId nobody can reproduce.
 */
export function canonicalizeManifest(entries) {
  const out = [];
  const seen = new Set();
  for (const entry of entries || []) {
    const path = normalizeSitePath(entry?.path);
    if (!path) throw new Error(`Unsafe or invalid site path: ${JSON.stringify(entry?.path)}`);
    if (seen.has(path)) throw new Error(`Duplicate path in manifest: ${path}`);
    seen.add(path);
    const hash = String(entry?.hash || '').toLowerCase();
    if (!BLOB_HASH_RE.test(hash)) throw new Error(`Bad content hash for ${path}`);
    const size = Number(entry?.size);
    if (!Number.isInteger(size) || size < 0) throw new Error(`Bad size for ${path}`);
    out.push({ path, hash, size });
  }
  out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  return out;
}

/**
 * deployId = sha256 over the canonical manifest. Pure function of the bytes
 * being served -- see the module header for why artifactHash cannot do this job.
 */
export async function computeDeployId(entries) {
  const canonical = canonicalizeManifest(entries);
  const payload = canonicalStringify({
    v: DEPLOY_MANIFEST_VERSION,
    files: canonical.map((entry) => [entry.path, entry.hash, entry.size])
  });
  return (await sha256Hex(payload)).slice(0, DEPLOY_ID_LENGTH);
}

/** R2 key layout. Keyed on siteId (a uuid), NEVER the slug — see migration 009. */
export function blobKey(siteId, hash) {
  return `sites/${siteId}/blobs/${hash}`;
}

export function manifestKey(siteId, deployId) {
  return `sites/${siteId}/deploys/${deployId}/manifest.json`;
}
